import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm,
  Tag,
  Card,
  Typography
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import apiService from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Publication {
  _id: string;
  title: string;
  description: string;
  year: number;
  category: string;
  content: {
    type: 'text' | 'image' | 'video' | 'link' | 'mixed';
    text?: string;
    images?: Array<{
      url: string;
      caption: string;
      alt: string;
    }>;
    video?: {
      url: string;
      title: string;
      description: string;
    };
    link?: {
      url: string;
      title: string;
      description: string;
    };
    mixed?: {
      text: string;
      images: Array<{
        url: string;
        caption: string;
        alt: string;
      }>;
      videos: Array<{
        url: string;
        title: string;
        description: string;
      }>;
      links: Array<{
        url: string;
        title: string;
        description: string;
      }>;
    };
  };
  authors: string | string[];
  tags: string[];
  isPublished: boolean;
  journal?: string;
  impact?: string;
  abstract?: string;
  doi?: string;
  coverImage?: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

const PublicationManagement: React.FC = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPublication, setEditingPublication] = useState<Publication | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  
  // 富文本编辑器状态
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [html, setHtml] = useState('');

  // 富文本编辑器配置
  const editorConfig: Partial<IEditorConfig> = {
    placeholder: '请输入详细内容...',
    MENU_CONF: {
      uploadImage: {
        server: '/api/upload/image', // 图片上传接口
        fieldName: 'image',
        maxFileSize: 10 * 1024 * 1024, // 10MB
        maxNumberOfFiles: 10,
        allowedFileTypes: ['image/*'],
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        withCredentials: true,
        timeout: 5 * 1000, // 5秒超时
        onBeforeUpload(file: File) {
          console.log('准备上传图片:', file);
          return file;
        },
        onProgress(progress: number, file: File) {
          console.log('上传进度:', progress, file);
        },
        onSuccess(file: File, res: any) {
          console.log('上传成功:', file, res);
        },
        onFailed(file: File, res: any) {
          console.log('上传失败:', file, res);
          message.error('图片上传失败');
        },
        onError(file: File, err: any, res: any) {
          console.log('上传错误:', file, err, res);
          message.error('图片上传出错');
        },
        customInsert(res: any, insertFn: any) {
          // 自定义插入图片的逻辑
          if (res.success && res.data && res.data.url) {
            insertFn(res.data.url, res.data.alt || '', res.data.href || '');
          } else {
            message.error('图片插入失败');
          }
        }
      }
    }
  };

  const toolbarConfig: Partial<IToolbarConfig> = {
    excludeKeys: [
      'insertTable',
      'codeBlock',
      'fullScreen'
    ]
  };

  useEffect(() => {
    fetchPublications();
  }, []);

  // 清理富文本编辑器
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<any>('/research');
      
      // 后端返回的是按年份分组的数据，需要转换为平铺数组
      let publicationsArray: Publication[] = [];
      
      if (response.success && response.data) {
        // 将分组数据转换为平铺数组
        Object.values(response.data).forEach((yearData: any) => {
          if (Array.isArray(yearData)) {
            publicationsArray = publicationsArray.concat(yearData);
          }
        });
      }
      
      console.log('获取到的原始数据:', response.data);
      console.log('转换后的数组:', publicationsArray);
      
      setPublications(publicationsArray);
    } catch (error) {
      message.error('获取研究成果列表失败');
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPublication(null);
    form.resetFields();
    setHtml(''); // 清空富文本编辑器
    setModalVisible(true);
  };

  const handleEdit = (record: Publication) => {
    setEditingPublication(record);
    
    // 处理表单数据映射
    const formData = {
      ...record,
      // 处理authors字段：如果是字符串，转换为数组
      authors: typeof record.authors === 'string' 
        ? record.authors.split(', ').filter(a => a.trim())
        : record.authors,
      // 处理content字段：从content.text获取值
      content: record.content?.text || '',
      // 处理keywords字段：从tags获取值
      keywords: record.tags || []
    };
    
    // 设置富文本编辑器的内容
    setHtml(record.content?.text || '');
    
    console.log('编辑数据:', record);
    console.log('表单数据:', formData);
    
    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`/research/${id}`);
      message.success('删除成功');
      fetchPublications();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // 验证富文本编辑器内容
      if (!html.trim()) {
        message.error('请输入详细内容');
        return;
      }
      
      // 处理authors字段：如果是数组，转换为字符串
      const submitData = {
        ...values,
        authors: Array.isArray(values.authors) ? values.authors.join(', ') : values.authors,
        // 添加默认值和必需字段
        isPublished: true,
        content: {
          type: 'text',
          text: html || values.content || values.abstract || '' // 优先使用富文本编辑器的内容
        },
        tags: Array.isArray(values.keywords) ? values.keywords : [],
        journal: values.journal || '未知期刊', // 添加默认的journal值
        impact: values.impact || 'IF: 0.000', // 添加默认的impact值
        abstract: values.abstract || '',
        coverImage: values.coverImage || '',
        doi: values.doi || ''
      };
      
      console.log('提交的数据:', submitData);
      
      if (editingPublication) {
        await apiService.put(`/research/${editingPublication._id}`, submitData);
        message.success('更新成功');
      } else {
        await apiService.post('/research', submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchPublications();
    } catch (error) {
      console.error('提交失败:', error);
      message.error('操作失败');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      '论文发表': 'blue',
      '专利申请': 'green',
      '项目成果': 'orange',
      '获奖成果': 'purple',
      '技术突破': 'red',
      '其他': 'default'
    };
    return colors[category as keyof typeof colors] || 'default';
  };

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      ellipsis: true,
      render: (text: string) => (
        <div style={{ fontWeight: 'bold' }} title={text}>
          {text}
        </div>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => (
        <Tag color={getCategoryColor(category)}>
          {category}
        </Tag>
      ),
    },
    {
      title: '作者',
      dataIndex: 'authors',
      key: 'authors',
      width: 120,
      ellipsis: true,
      render: (authors: string | string[]) => {
        // 处理authors可能是字符串或数组的情况
        if (typeof authors === 'string') {
          return <Tag title={authors}>{authors}</Tag>;
        }
        if (Array.isArray(authors)) {
          const displayAuthors = authors.slice(0, 1); // 只显示第一个作者
          return (
            <Space>
              <Tag title={authors.join(', ')}>{displayAuthors[0]}</Tag>
              {authors.length > 1 && <Tag color="default">+{authors.length - 1}</Tag>}
            </Space>
          );
        }
        return <Tag>{String(authors)}</Tag>;
      },
    },
    {
      title: '期刊/会议',
      dataIndex: 'journal',
      key: 'journal',
      width: 120,
      ellipsis: true,
    },
    {
      title: '年份',
      dataIndex: 'year',
      key: 'year',
      width: 70,
    },
    {
      title: '影响因子',
      dataIndex: 'impact',
      key: 'impact',
      width: 100,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: Publication) => (
        <Space size="small">
          <Button 
            type="link" 
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/research-achievements/${record._id}`)}
          >
            查看
          </Button>
          <Button 
            type="link" 
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个研究成果吗？"
            onConfirm={() => handleDelete(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={3}>研究成果管理</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加研究成果
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={Array.isArray(publications) ? publications : []}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={editingPublication ? '编辑研究成果' : '添加研究成果'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setHtml(''); // 清空富文本编辑器内容
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入研究成果标题" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea rows={3} placeholder="请输入研究成果描述" />
          </Form.Item>

          <Form.Item
            name="category"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="请选择分类">
              <Option value="论文发表">论文发表</Option>
              <Option value="专利申请">专利申请</Option>
              <Option value="项目成果">项目成果</Option>
              <Option value="获奖成果">获奖成果</Option>
              <Option value="技术突破">技术突破</Option>
              <Option value="其他">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="authors"
            label="作者"
            rules={[{ required: true, message: '请输入作者' }]}
          >
            <Select
              mode="tags"
              placeholder="请输入作者"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="journal"
            label="期刊/会议"
            rules={[{ required: true, message: '请输入期刊或会议名称' }]}
          >
            <Input placeholder="请输入期刊或会议名称" />
          </Form.Item>

          <Form.Item
            name="year"
            label="年份"
            rules={[{ required: true, message: '请输入年份' }]}
          >
            <Input type="number" min={2000} max={new Date().getFullYear() + 1} placeholder="请输入年份" />
          </Form.Item>

          <Form.Item
            name="doi"
            label="DOI"
          >
            <Input placeholder="请输入DOI" />
          </Form.Item>

          <Form.Item
            name="impact"
            label="影响因子"
            rules={[{ required: true, message: '请输入影响因子' }]}
          >
            <Input placeholder="例如：IF: 67.439" />
          </Form.Item>

          <Form.Item
            name="abstract"
            label="摘要"
            rules={[{ required: true, message: '请输入摘要' }]}
          >
            <TextArea rows={4} placeholder="请输入摘要" />
          </Form.Item>

          <Form.Item
            label="详细内容"
            required
            validateStatus={html.trim() ? '' : 'error'}
            help={html.trim() ? '' : '请输入详细内容'}
          >
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}>
              <Toolbar
                editor={editor}
                defaultConfig={toolbarConfig}
                mode="default"
                style={{ borderBottom: '1px solid #d9d9d9' }}
              />
              <Editor
                defaultConfig={editorConfig}
                value={html}
                onCreated={setEditor}
                onChange={editor => setHtml(editor.getHtml())}
                mode="default"
                style={{ height: '300px', overflowY: 'hidden' }}
              />
            </div>
          </Form.Item>

          <Form.Item
            name="keywords"
            label="关键词"
          >
            <Select
              mode="tags"
              placeholder="请输入关键词"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="coverImage"
            label="封面图片URL"
          >
            <Input placeholder="请输入封面图片URL" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPublication ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PublicationManagement; 